/**
 * The weekly EI benefit amount, derived from the rate and period of pay the
 * citizen enters on the application's "Your last job" card.
 *
 * Shared by the BFF (the amount stamped on the claim at submit) and the
 * wizard's review step (the pre-submit estimate, spec 014), so both show
 * exactly the same figure for the same inputs. Moved here verbatim from
 * `apps/employment-insurance-bff/src/data.ts`; behaviour unchanged.
 *
 * A deliberately simplified illustrative calculation, not the real EI
 * best-weeks-average formula (which derives insurable earnings from the
 * claimant's best ~14-22 weeks over the last 52) -- this PoT has no
 * payroll history to average over, just the single rate/period entered on
 * the application. Clamped to a plausible EI-like weekly benefit range so
 * the demo confirmation still reads as realistic.
 */
export type PayPeriod = 'hourly' | 'weekly' | 'biweekly' | 'monthly';

export interface BenefitEstimateInput {
  payRate: number;
  payPeriod: PayPeriod;
}

const WEEKLY_BENEFIT_RATE = 0.55;
const MIN_WEEKLY_BENEFIT = 100;
const MAX_WEEKLY_BENEFIT = 668;
const HOURS_PER_WEEK = 37.5;

function weeklyEquivalent(payRate: number, payPeriod: PayPeriod): number {
  switch (payPeriod) {
    case 'hourly':
      return payRate * HOURS_PER_WEEK;
    case 'weekly':
      return payRate;
    case 'biweekly':
      return payRate / 2;
    case 'monthly':
      return (payRate * 12) / 52;
  }
}

export function calculateWeeklyBenefitAmount({ payRate, payPeriod }: BenefitEstimateInput): number {
  const weekly = weeklyEquivalent(payRate, payPeriod);
  const benefit = weekly * WEEKLY_BENEFIT_RATE;
  return Math.round(Math.min(MAX_WEEKLY_BENEFIT, Math.max(MIN_WEEKLY_BENEFIT, benefit)) * 100) / 100;
}
